/**
 *  @swagger
 *  definitions:
 *    EntryParams:
 *      type: object
 *      properties:
 *        RegionId:
 *          description: ID of the associated Region
 *          type: string
 *        Name:
 *          description: Name of the Entry
 *          type: string
 *        Image:
 *          description: Image of the Entry
 *          type: string
 *        RegistrationNo:
 *          description: Registration No of the Entry
 *          type: string
 *        SailNo:
 *          description: Sail No of the Entry
 *          type: string
 *        Model:
 *          description: Model of the Entry
 *          type: string
 *        Harbor:
 *          description: Harbor of the Entry
 *          type: string
 *        Club:
 *          description: Club of the Entry
 *          type: string
 *
 *  @swagger
 *  definitions:
 *    EntryCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/EntryParams'
 *        - type: object
 *          required:
 *            - RegionId
 *            - RegistrationNo
 *
 *  @swagger
 *  definitions:
 *    EntryQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the Entry
 *              type: string
 *        - $ref: '#/definitions/EntryParams'
 *
 *  @swagger
 *  definitions:
 *    EntryUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/EntryQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    Entry:
 *      allOf:
 *        - $ref: '#/definitions/EntryCreateParams'
 *        - $ref: '#/definitions/EntryUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      Entry:
 *        allOf:
 *          - $ref: '#/definitions/Entry'
 *          - type: object
 *            properties:
 *              Region:
 *                description: Region of the Entry
 *                $ref: '#/components/schemas/Region'
 *              RegattaEntries:
 *                description: RegattaEntries of the Entry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/RegattaEntry'
 *              Ratings:
 *                description: Ratings of the Entry
 *                type: array
 *                items:
 *                  $ref: '#/components/schemas/Rating'
 */

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegionId: string;
};

export type $Params = {
  Club: null | string;
  Harbor: null | string;
  Image: null | string;
  Model: null | string;
  Name: null | string;
  RegistrationNo: string;
  SailNo: null | string;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Ratings?: Array<$Model<'Rating'>>;
  RegattaEntries?: Array<$Model<'RegattaEntry'>>;
  Region?: $Model<'Region'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Club: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Harbor: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Image: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Model: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Name: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  RegistrationNo: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  SailNo: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'Entry'> => {
  const Entry = stack.define(
    'Entry',
    fields(Sequelize),
    {
      tableName: 'Entries',
    },
  );

  Entry.associate = (models: $ModelClasses) => {
    models.Entry.belongsTo(
      models.Region,
      {
        as: 'Region',
        foreignKey: 'RegionId',
      },
    );
    models.Entry.hasMany(
      models.RegattaEntry,
      {
        as: 'RegattaEntries',
        foreignKey: 'EntryId',
      },
    );
    models.Entry.belongsToMany(
      models.Rating,
      {
        as: 'Ratings',
        foreignKey: 'EntryId',
        otherKey: 'RatingId',
        through: models.EntryRating,
      },
    );
  };

  Entry.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return Entry;
});
